import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { RootState } from "src/store";

interface CitationLoaderStateArgs {
	/** True once the tag has usable reference / widget / chart data. */
	hasData: boolean;
	/** Whether the owning tab's reference window is still loading. Widget tags omit this (defaults false). */
	isReferenceLoading?: boolean;
	/** Whether the reference window has reached a terminal state (resolved / error / cancelled). */
	isReferenceTerminal?: boolean;
	/** For grouped doc citations: whether every expected ref has arrived. Defaults true. */
	hasAllExpectedReferences?: boolean;
	/**
	 * Per-message completion override. When provided (harness turns pass the owning
	 * message's own Answer-tab completion), it scopes the "process complete" fallback
	 * to THIS message — so a *different* message streaming, which flips the global
	 * `isProcessComplete` flag, can no longer re-show loaders on an already-finalized
	 * message. Undefined → fall back to the global askKuralSlice flag (legacy behavior).
	 */
	isProcessComplete?: boolean;
}

/**
 * Shared loader-visibility decision for the inline citation tags
 * (InlineReferenceTag / InlineWidgetTag / InlineQuantCitationTag). Extracted so all
 * three behave identically — previously each hand-rolled this same precedence plus a
 * `wasProcessedRef` latch, which drifted and caused recurring loader regressions.
 *
 * Precedence (unchanged from the original three implementations):
 *  1. Have usable data AND (not loading, or all expected refs present, or terminal) -> no loader.
 *  2. Already shown once without a loader (latched) -> no loader.
 *  3. Terminal -> latch + no loader.
 *  4. Still reference-loading -> show loader.
 *  5. Global `isProcessComplete` legacy fallback -> latch + no loader.
 *  6. Otherwise (first-time load) -> show loader.
 */
export function useCitationLoaderState({
	hasData,
	isReferenceLoading = false,
	isReferenceTerminal = false,
	hasAllExpectedReferences = true,
	isProcessComplete: isProcessCompleteOverride,
}: CitationLoaderStateArgs): boolean {
	// OR (not ??) on purpose: the override only ADDS "this message is already
	// complete" on top of the global flag. So behavior changes ONLY in the leak
	// scenario (a finalized message while a sibling streams: override true, global
	// false) and is identical everywhere else — a streaming message passes false and
	// still tracks the global flag exactly as before.
	const globalProcessComplete = useSelector((state: RootState) => state.askKuralSlice.isProcessComplete);
	const isProcessComplete = Boolean(isProcessCompleteOverride) || globalProcessComplete;
	const wasProcessedRef = useRef(false);

	// Latch once the tag has data so a later data-loss can't re-show the loader.
	if (hasData) {
		wasProcessedRef.current = true;
	}

	return useMemo(() => {
		if (hasData && (!isReferenceLoading || hasAllExpectedReferences || isReferenceTerminal)) {
			return false;
		}
		if (wasProcessedRef.current) return false;
		if (isReferenceTerminal) {
			wasProcessedRef.current = true;
			return false;
		}
		if (isReferenceLoading) return true;
		if (isProcessComplete) {
			wasProcessedRef.current = true;
			return false;
		}
		return true;
	}, [hasData, isReferenceLoading, isReferenceTerminal, hasAllExpectedReferences, isProcessComplete]);
}
